import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const AddClient = ({ onStatsUpdate }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    client_name: '',
    calendly_username: '',
    calendly_password: '',
    planet_username: '',
    planet_password: '',
    planet_mobile_link: '',
    booker1_impact_username: '',
    booker1_impact_password: '',
    impact_mobile_link: '',
    booker2_impact_username: '',
    booker2_impact_password: '',
    calendly_link: '',
    misc_notes: '',
    video_intro: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axiosInstance.post(`${API}/clients`, formData);
      toast.success('Manager added successfully');
      onStatsUpdate();
      navigate('/');
    } catch (error) {
      console.error('Failed to add manager:', error);
      toast.error(error.response?.data?.detail || 'Failed to add manager');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <Button onClick={() => navigate('/')} className="btn-gold-outline" size="icon" data-testid="back-to-clients-btn">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h2 className="text-2xl font-bold title-gold">Add New Manager</h2>
      </div>

      <Card className="beast-card">
        <CardHeader>
          <CardTitle className="title-gold">Manager Information</CardTitle>
          <CardDescription className="text-slate-300">Enter the manager's details and account credentials</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="client_name" className="text-slate-200">Manager Name *</Label>
              <Input
                id="client_name"
                type="text"
                placeholder="e.g., John Manager"
                value={formData.client_name}
                onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                required
                className="input-dark"
                data-testid="add-client-name-input"
              />
            </div>

            {/* Misc Notes Section - Admin Only */}
            <div className="border-t border-slate-700 pt-4">
              <h3 className="font-semibold title-gold mb-3">Misc Notes (Admin Only)</h3>
              <div className="space-y-2">
                <Label htmlFor="misc_notes" className="text-slate-200">Notes for Bookers</Label>
                <Textarea
                  id="misc_notes"
                  placeholder="e.g., State: California, Timezone: PST, Special instructions..."
                  value={formData.misc_notes}
                  onChange={(e) => setFormData({ ...formData, misc_notes: e.target.value })}
                  className="input-dark min-h-[100px]"
                  data-testid="add-misc-notes-input"
                />
                <p className="text-xs text-slate-400">This will be visible to all users but only editable by admins</p>
              </div>
            </div>

            {/* Video Introduction Section */}
            <div className="border-t border-slate-700 pt-4">
              <h3 className="font-semibold title-gold mb-3">Video Introduction</h3>
              <div className="space-y-2">
                <Label htmlFor="video_intro" className="text-slate-200">Video Introduction URL</Label>
                <Input
                  id="video_intro"
                  type="url"
                  placeholder="e.g., https://www.loom.com/share/..."
                  value={formData.video_intro}
                  onChange={(e) => setFormData({ ...formData, video_intro: e.target.value })}
                  className="input-dark"
                  data-testid="add-video-intro-input"
                />
                <p className="text-xs text-slate-400">Add a video introduction link (Loom, YouTube, etc.)</p>
              </div>
            </div>

            <div className="border-t border-slate-700 pt-4">
              <h3 className="font-semibold title-gold mb-3">Calendly Account</h3>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="calendly_username" className="text-slate-200">Username</Label>
                  <Input
                    id="calendly_username"
                    type="text"
                    placeholder="Calendly username"
                    value={formData.calendly_username}
                    onChange={(e) => setFormData({ ...formData, calendly_username: e.target.value })}
                    className="input-dark"
                    data-testid="add-calendly-username-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="calendly_password" className="text-slate-200">Password</Label>
                  <Input
                    id="calendly_password"
                    type="text"
                    placeholder="Calendly password"
                    value={formData.calendly_password}
                    onChange={(e) => setFormData({ ...formData, calendly_password: e.target.value })}
                    className="input-dark"
                    data-testid="add-calendly-password-input"
                  />
                <div className="space-y-2">
                  <Label htmlFor="calendly_link" className="text-slate-200">Calendly Link</Label>
                  <Input
                    id="calendly_link"
                    type="text"
                    placeholder="www.calendly.com/username"
                    value={formData.calendly_link}
                    onChange={(e) => setFormData({ ...formData, calendly_link: e.target.value })}
                    className="input-dark"
                    data-testid="add-calendly-link-input"
                  />
                </div>
                </div>
              </div>
            </div>

            <div className="border-t border-slate-700 pt-4">
              <h3 className="font-semibold title-gold mb-3">Planet Account</h3>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="planet_username" className="text-slate-200">Username</Label>
                  <Input
                    id="planet_username"
                    type="text"
                    placeholder="Planet username"
                    value={formData.planet_username}
                    onChange={(e) => setFormData({ ...formData, planet_username: e.target.value })}
                    className="input-dark"
                    data-testid="add-planet-username-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="planet_password" className="text-slate-200">Password</Label>
                  <Input
                    id="planet_password"
                    type="text"
                    placeholder="Planet password"
                    value={formData.planet_password}
                    onChange={(e) => setFormData({ ...formData, planet_password: e.target.value })}
                    className="input-dark"
                    data-testid="add-planet-password-input"
                  />
                </div>
                <div className="pt-2 flex items-center gap-2">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_client-tracker-121/artifacts/9of3ndc3_20240408015249_600x600_ao_logo.png" 
                    alt="Planet" 
                    className="h-6 w-6 object-contain"
                  />
                  <a
                    href="https://m.planetaltig.com/Account/Login"
                    target="_blank"
                    rel="noreferrer"
                    className="title-gold font-semibold underline break-all hover:text-yellow-600"
                    data-testid="planet-mobile-link"
                  >
                    Open Planet Mobile
                  </a>
                </div>
              </div>
            </div>

            {/* IMPACT Mobile quick link placed above Booker 1 - IMPACT */}
            <div className="pt-2 flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_client-tracker-121/artifacts/jejboty7_GL-9c593a3d.png" 
                alt="IMPACT" 
                className="h-6 w-6 object-contain"
              />
              <a
                href="https://sso.ailife.com/ImpactPortal/SecureAuth.aspx?client_id=a97eeaf1c52946e4a438a67fb95061f0&redirect_uri=https%3A%2F%2Fimpact.ailife.com%2Fsignin&response_type=code%20id_token&scope=openid%20profile%20offline_access&response_mode=form_post&nonce=638956410121602807.M2ViODIxMDUtYzJhNy00N2IwLTg2ZDgtNWJjMmE0M2M1M2QzZjU0ODUzZGItMTM5My00MTIx-LWFkZGItZmE0MDI0YTVkZmE5&state=client-tracker-121&x-client-SKU=ID_NET8_0&x-client-ver=7.1.2.0"
                target="_blank"
                rel="noreferrer"
                className="title-gold font-semibold underline break-all hover:text-yellow-600"
                data-testid="impact-mobile-link"
              >
                Open IMPACT Mobile
              </a>
            </div>


            <div className="border-t border-slate-700 pt-4">
              <h3 className="font-semibold title-gold mb-3">Booker 1 - IMPACT (Female)</h3>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="booker1_impact_username" className="text-slate-200">Booker 1 Username</Label>
                  <Input
                    id="booker1_impact_username"
                    type="text"
                    placeholder="Booker 1 Username IMPACT"
                    value={formData.booker1_impact_username}
                    onChange={(e) => setFormData({ ...formData, booker1_impact_username: e.target.value })}
                    className="input-dark"
                    data-testid="add-booker1-impact-username-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="booker1_impact_password" className="text-slate-200">Booker 1 Password</Label>
                  <Input
                    id="booker1_impact_password"
                    type="text"
                    placeholder="Booker 1 Password IMPACT"
                    value={formData.booker1_impact_password}
                    onChange={(e) => setFormData({ ...formData, booker1_impact_password: e.target.value })}
                    className="input-dark"
                    data-testid="add-booker1-impact-password-input"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-slate-700 pt-4">
            <h3 className="font-semibold title-gold mb-3">Booker 2 - IMPACT (Male)</h3>
            <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="booker2_impact_username" className="text-slate-200">Booker 2 Username</Label>
                  <Input
                    id="booker2_impact_username"
                    type="text"
                    placeholder="Booker 2 Username IMPACT"
                    value={formData.booker2_impact_username}
                    onChange={(e) => setFormData({ ...formData, booker2_impact_username: e.target.value })}
                    className="input-dark"
                    data-testid="add-booker2-username-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="booker2_impact_password" className="text-slate-200">Booker 2 Password</Label>
                  <Input
                    id="booker2_impact_password"
                    type="text"
                    placeholder="Booker 2 Password IMPACT"
                    value={formData.booker2_impact_password}
                    onChange={(e) => setFormData({ ...formData, booker2_impact_password: e.target.value })}
                    className="input-dark"
                    data-testid="add-booker2-password-input"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                onClick={() => navigate('/')}
                className="flex-1 btn-gold-outline"
                disabled={loading}
                data-testid="cancel-add-client-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 btn-gold"
                disabled={loading}
                data-testid="submit-add-client-btn"
              >
                {loading ? 'Adding...' : 'Add Manager'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddClient;